// Erster Meldungsschritt in der Schadenmappe (intern statt `StepType`):
// Kontakt, Risikoobjekt und Vertrag, dann die Schadenart des Wizards und die
// Sparte (Damage.damageType-Liste aus `schadensarten.ts`). Vor der Anlage ist
// der Kontakt Pflicht; danach bleibt alles änderbar — was die Seite daraus
// speichert, entscheidet sie (Kontakt/Risiko/Vertrag sofort, der Rest per Autosave).
//
// Seit der neuen Schrittfolge (13.10.2026) zeigt die Mappe das als drei
// Schritte (`teil`): „Kontakt“, „Schadenart“ (Art + konkrete Schadenart) und
// „Risiko & Vertrag“ — dort wird „Wo“ aus der Anschrift des Risikos vorbelegt.
import * as React from "react";
import { fetchJson, linkQueryUrls, risikoRefFromRow, vertragRefFromRow } from "../../vorgang/detail/linkFetch";
import { risikoLabel, vertragLabel } from "../../vorgang/detail/linkSlots";
import { StepHead } from "../../schadenmeldung-big/fields";
import { StepType } from "../../schadenmeldung-big/steps/shared";
import { SCHADENSART_SONSTIGE, schadensartenFor } from "../../schadenmeldung-big/schadensarten";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import { KontaktSuche, useKontaktName, type Verweis } from "./KontaktSuche";
import { AuswahlListe, type AuswahlEintrag } from "./AuswahlListe";
import {
  anschriftText,
  risikoAnschrift,
  risikoPasstZurSchadenart,
  woVorbelegung,
  type Anschrift,
} from "./vorbelegung";

export type { Verweis };

export interface SchadenartInternProps {
  answers: BigAnswers;
  update: (patch: Partial<BigAnswers>) => void;
  schadensart: string | null;
  onSchadensart: (s: string | null) => void;
  kontakt: Verweis | null;
  onKontakt: (k: Verweis | null) => void;
  risiko: Verweis | null;
  onRisiko: (r: Verweis | null) => void;
  vertrag: Verweis | null;
  onVertrag: (v: Verweis | null) => void;
  /**
   * Kontakt, den der Server hat (von der Seite; `null` vor der Anlage). Gegen
   * ihn entscheidet sich „anderer Kontakt“, und „Abbrechen“ führt zu ihm
   * zurück — unabhängig davon, ob dieser Schritt zwischendurch verlassen wurde.
   */
  gespeicherterKontakt?: Verweis | null;
  /**
   * Schrittfolge der Mappe (13.10.2026): Kontakt, Schadenart und Risiko &
   * Vertrag sind je ein eigener Schritt. Ohne Angabe steht alles auf einer Seite.
   */
  teil?: "kontakt" | "art" | "risiko";
}

/** Risiko des Kontakts mit Risikoart und Anschrift (Filter nach Schadenart, Vorbelegen von „Wo“). */
interface RisikoEintrag extends Verweis {
  riskType: string | null;
  anschrift: Anschrift | null;
  /** Nebenzeile der Liste: Anschrift bzw. Kennzeichen, dazu die Risikoart. */
  neben: string;
}
/** Vertrag mit den Ids seiner Risiken und den Texten für die Liste. */
interface VertragEintrag extends Verweis {
  risikoIds: number[];
  /** Hauptzeile der Liste: Produkt/Sparte, sonst die Vertragsnummer. */
  titel: string;
  /** Nebenzeile: Sparte · Vertragsnummer · Versicherer, soweit vorhanden. */
  neben: string;
}

const merkmal = (row: any, name: string): string => {
  const liste: any[] = Array.isArray(row?.characteristics) ? row.characteristics : [];
  const v = liste.find((m) => m?.nameBezeichnung === name)?.riskCharacteristicValue;
  return typeof v === "string" ? v.trim() : "";
};

function risikoEintrag(row: any): RisikoEintrag {
  const ref = risikoRefFromRow(row);
  const anschrift = risikoAnschrift(row);
  const riskType = ref.riskType ?? null;
  const merk = anschriftText(anschrift) || merkmal(row, "licensePlate") || merkmal(row, "Risikoort");
  return {
    id: ref.id,
    label: risikoLabel(ref),
    riskType,
    anschrift,
    neben: [merk, riskType].filter(Boolean).join(" · "),
  };
}

function vertragEintrag(row: any): VertragEintrag {
  const ref = vertragRefFromRow(row);
  const label = vertragLabel(ref);
  const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const produkt = text(row?.bezeichnung) || text(row?.insuranceProduct?.name);
  const sparte = text(ref.branch);
  const titel = produkt || sparte || label;
  const versicherer = text(row?.insurer?.displayName) || text(row?.gesellschaft?.name);
  return {
    id: ref.id,
    label,
    risikoIds: (ref.riskObjects ?? []).map((r) => Number(r.id)).filter((n) => n > 0),
    titel,
    neben: [sparte !== titel ? sparte : "", label !== titel ? label : "", versicherer].filter(Boolean).join(" · "),
  };
}

/** Wie die Schadenart die Risiken eingrenzt — für Leerzustand und Schalter. */
const RISIKO_ART_TEXT: Partial<Record<NonNullable<BigAnswers["damageType"]>, string>> = {
  gebaeude: "Gebäude",
  kfz: "Fahrzeuge",
};

/** Die jüngste Version eines Callbacks — für Effekte, die nicht bei jedem Render neu laufen sollen. */
function useAktuell<T>(wert: T): React.MutableRefObject<T> {
  const ref = React.useRef(wert);
  React.useEffect(() => {
    ref.current = wert;
  });
  return ref;
}

/** Auswahl-Chip (`.chip` + Aktiv-Zustand). */
function Wahl({ aktiv, onClick, children }: { aktiv: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className={`chip smp-wahl${aktiv ? " ist-aktiv" : ""}`}
      aria-pressed={aktiv}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Chips aus einer Liste des Kontakts, „keins“ vorneweg. */
function ChipAuswahl({
  titel,
  eintraege,
  gewaehlt,
  onWahl,
  hinweis,
}: {
  titel: string;
  eintraege: Verweis[] | null;
  gewaehlt: Verweis | null;
  onWahl: (v: Verweis | null) => void;
  /** Zeile unter den Chips (z. B. „keine hinterlegt“). */
  hinweis?: string | null;
}) {
  // Die Vorauswahl steht auch dann da, wenn sie nicht (mehr) in der Liste ist.
  const liste = eintraege ?? [];
  const sichtbar =
    gewaehlt && !liste.some((e) => e.id === gewaehlt.id)
      ? [...liste, { id: gewaehlt.id, label: gewaehlt.label || `#${gewaehlt.id}` }]
      : liste;
  return (
    <div className="smp-feld">
      <div className="smp-feld-titel">
        {titel} <span className="smp-optional">optional</span>
      </div>
      <div className="smp-chips" role="group" aria-label={titel}>
        <Wahl aktiv={gewaehlt === null} onClick={() => onWahl(null)}>
          keins
        </Wahl>
        {sichtbar.map((e) => (
          <Wahl key={e.id} aktiv={gewaehlt?.id === e.id} onClick={() => onWahl({ id: e.id, label: e.label })}>
            {e.label}
          </Wahl>
        ))}
        {eintraege === null && <span className="smp-hinweis">lädt …</span>}
      </div>
      {hinweis && <div className="smp-hinweis">{hinweis}</div>}
    </div>
  );
}

function Sparte({
  damageType,
  wert,
  onWert,
  titel = "Sparte",
}: {
  damageType: BigAnswers["damageType"];
  wert: string | null;
  onWert: (s: string) => void;
  titel?: string;
}) {
  const optionen = schadensartenFor(damageType);
  const v = wert ?? "";
  // Der Wert ist die einzige Quelle: ein Wert außerhalb der Liste ist Freitext.
  const eigen = v !== "" && !optionen.includes(v);
  const sonstigeAktiv = v === SCHADENSART_SONSTIGE || eigen;
  return (
    <div className="smp-feld">
      <div className="smp-feld-titel">{titel}</div>
      <div className="smp-chips" role="group" aria-label={titel}>
        {optionen.map((o) => (
          <Wahl key={o} aktiv={o === SCHADENSART_SONSTIGE ? sonstigeAktiv : v === o} onClick={() => onWert(o)}>
            {o}
          </Wahl>
        ))}
      </div>
      {sonstigeAktiv && (
        <label className="smp-freitext">
          <span className="smp-feld-titel">{titel} (Freitext)</span>
          <input
            type="text"
            className="smp-suche-feld"
            value={eigen ? v : ""}
            placeholder={`${titel} eingeben`}
            onChange={(e) => onWert(e.target.value || SCHADENSART_SONSTIGE)}
          />
        </label>
      )}
    </div>
  );
}

export function SchadenartIntern(props: SchadenartInternProps) {
  const { answers, update, schadensart, onSchadensart, kontakt, onKontakt, risiko, onRisiko, vertrag, onVertrag } =
    props;
  const kontaktId = kontakt?.id ?? null;
  const teil = props.teil ?? null;
  // Risiken und Verträge braucht nur der Schritt, der sie zeigt.
  const mitListen = teil === null || teil === "risiko";
  const aufrufe = useAktuell({ onRisiko, onVertrag });
  // In „Schadenart“ und „Risiko & Vertrag“ gibt es keine Kontaktsuche, die den
  // Namen einer Vorauswahl nachlädt — die Kopfzeile braucht ihn trotzdem.
  useKontaktName(teil === "art" || teil === "risiko" ? kontakt : null, onKontakt);

  // Risikoobjekte und Verträge des Kontakts (`null` = lädt).
  const [listen, setListen] = React.useState<{
    kontaktId: number;
    risiken: RisikoEintrag[] | null;
    vertraege: VertragEintrag[] | null;
  } | null>(null);
  React.useEffect(() => {
    if (kontaktId === null || !mitListen) return;
    let abgebrochen = false;
    setListen({ kontaktId, risiken: null, vertraege: null });
    void fetchJson(linkQueryUrls.risksByContact(kontaktId)).then((rows) => {
      if (abgebrochen) return;
      const risiken = rows.map(risikoEintrag);
      setListen((l) => (l && l.kontaktId === kontaktId ? { ...l, risiken } : l));
    });
    void fetchJson(linkQueryUrls.contractsByContact(kontaktId)).then((rows) => {
      if (abgebrochen) return;
      const vertraege = rows.map(vertragEintrag);
      setListen((l) => (l && l.kontaktId === kontaktId ? { ...l, vertraege } : l));
    });
    return () => {
      abgebrochen = true;
    };
  }, [kontaktId, mitListen]);
  const eigeneListen = listen && listen.kontaktId === kontaktId ? listen : null;
  const risiken = eigeneListen?.risiken ?? null;
  const vertraege = eigeneListen?.vertraege ?? null;

  // Namen einer Vorauswahl aus den geladenen Chips übernehmen.
  React.useEffect(() => {
    if (!risiko || risiko.label !== "" || !risiken) return;
    const treffer = risiken.find((r) => r.id === risiko.id);
    if (treffer) aufrufe.current.onRisiko({ id: treffer.id, label: treffer.label });
  }, [risiko, risiken, aufrufe]);
  React.useEffect(() => {
    if (!vertrag || vertrag.label !== "" || !vertraege) return;
    const treffer = vertraege.find((v) => v.id === vertrag.id);
    if (treffer) aufrufe.current.onVertrag({ id: treffer.id, label: treffer.label });
  }, [vertrag, vertraege, aufrufe]);

  // Bezug für „anderer Kontakt“: nach der Anlage der Kontakt des Servers (die
  // Seite ist dafür die einzige Wahrheit — ein lokaler Merker ginge beim
  // Verlassen des Schritts verloren), vor der Anlage der aktuell gewählte.
  // Nach „Kontakt ändern“ ist `kontakt` lokal `null`; ohne Server-Bezug zählt
  // dann jeder gewählte Kontakt als anderer.
  const gespeicherterKontakt = props.gespeicherterKontakt ?? null;
  const bezugId = gespeicherterKontakt?.id ?? kontaktId;

  const kontaktWaehlen = (k: Verweis | null) => {
    // Ein anderer Kontakt: Risiko und Vertrag gehörten zum alten. Reihenfolge
    // bewusst: erst Risiko/Vertrag leeren, dann den Kontakt — die Seite schickt
    // die Sofort-PUTs in Aufrufreihenfolge (`riskObject`/`contract` vor `contact`),
    // so hängt auf dem Server nie ein Risiko/Vertrag des alten am neuen Kontakt.
    if (k !== null && k.id !== bezugId) {
      if (risiko !== null) onRisiko(null);
      if (vertrag !== null) onVertrag(null);
    }
    onKontakt(k);
  };

  // ---- Schritt „Risiko & Vertrag“ ----
  const gewaehltesRisiko = risiko ? risiken?.find((r) => r.id === risiko.id) ?? null : null;
  const risikoId = risiko?.id ?? null;

  // Verträge ZU DIESEM RISIKO: `contracts/by-risk/:id` (Primärvertrag des
  // Risikos ∪ n:m-Verknüpfung `RiskObjectContract`). `null` = lädt.
  const [zumRisiko, setZumRisiko] = React.useState<{ risikoId: number; vertraege: VertragEintrag[] | null } | null>(
    null,
  );
  /** Risiko, das gerade von Hand gewählt wurde — nur dann wird der Vertrag nachgezogen. */
  const frischGewaehlt = React.useRef<number | null>(null);
  const vertragRef = useAktuell(vertrag);
  React.useEffect(() => {
    if (teil !== "risiko" || risikoId === null) return;
    let abgebrochen = false;
    setZumRisiko({ risikoId, vertraege: null });
    void fetchJson(linkQueryUrls.contractsByRisk(risikoId)).then((rows) => {
      if (abgebrochen) return;
      const liste = rows.map(vertragEintrag);
      setZumRisiko((z) => (z && z.risikoId === risikoId ? { ...z, vertraege: liste } : z));
      if (frischGewaehlt.current !== risikoId) return;
      frischGewaehlt.current = null;
      // Von Hand auf dieses Risiko gewechselt: genau ein Vertrag → übernehmen;
      // ein gewählter Vertrag, der nicht zu diesem Risiko gehört, verfällt.
      const jetzt = vertragRef.current;
      if (liste.length === 1) {
        if (jetzt?.id !== liste[0].id) aufrufe.current.onVertrag({ id: liste[0].id, label: liste[0].label });
      } else if (jetzt && !liste.some((c) => c.id === jetzt.id)) {
        aufrufe.current.onVertrag(null);
      }
    });
    return () => {
      abgebrochen = true;
    };
  }, [teil, risikoId, aufrufe, vertragRef]);
  const vertraegeZumRisiko = zumRisiko && zumRisiko.risikoId === risikoId ? zumRisiko.vertraege : null;

  const [alleRisiken, setAlleRisiken] = React.useState(false);
  const [alleVertraege, setAlleVertraege] = React.useState(false);

  const risikoWaehlen = (e: AuswahlEintrag | null) => {
    const neu = e ? risiken?.find((r) => r.id === e.id) ?? null : null;
    onRisiko(neu ? { id: neu.id, label: neu.label } : null);
    if (!neu) return;
    frischGewaehlt.current = neu.id;
    // „Wo“ vorbelegen: in leere Felder — oder über den Stand, der aus dem
    // zuvor gewählten Risiko stammt und nicht geändert wurde.
    const patch = woVorbelegung(answers, neu.anschrift, gewaehltesRisiko?.anschrift ?? null);
    if (patch) update(patch);
  };

  const typAendern = (patch: Partial<BigAnswers>) => {
    update(patch);
    // Die Sparte gehört zur Schadenart — bei einem Wechsel verfällt sie.
    if (patch.damageType !== undefined && patch.damageType !== answers.damageType && schadensart !== null) {
      onSchadensart(null);
    }
  };

  if (teil === "kontakt") {
    return (
      <div className="smp-schadenart">
        <StepHead
          eyebrow="Schadenmeldung"
          title="Für welchen Kontakt wird der Schaden gemeldet?"
          hint="Der Kontakt bestimmt, welche Risiken und Verträge danach zur Auswahl stehen."
        />
        <section className="smp-karte">
          <div className="smp-feld">
            <div className="smp-feld-titel">
              Kontakt <span className="smp-pflicht">Pflicht</span>
            </div>
            <KontaktSuche
              kontakt={kontakt}
              vorheriger={gespeicherterKontakt}
              onWahl={kontaktWaehlen}
              onName={onKontakt}
            />
          </div>
        </section>
      </div>
    );
  }

  if (teil === "art") {
    return (
      <div className="smp-schadenart">
        <StepType answers={answers} update={typAendern} />
        {answers.damageType && (
          <section className="smp-karte">
            <Sparte
              damageType={answers.damageType}
              wert={schadensart}
              onWert={onSchadensart}
              titel="Konkrete Schadenart"
            />
          </section>
        )}
      </div>
    );
  }

  if (teil === "risiko") {
    const artText = answers.damageType ? RISIKO_ART_TEXT[answers.damageType] ?? null : null;
    const passend = (risiken ?? []).filter((r) => risikoPasstZurSchadenart(answers.damageType, r.riskType));
    const gefiltert = artText !== null && !alleRisiken;
    const risikoBasis = risiken === null ? null : gefiltert ? passend : risiken;
    // Ein gewähltes Risiko steht immer in der Liste — auch außerhalb des Filters.
    const risikoListe: AuswahlEintrag[] | null =
      risikoBasis === null
        ? null
        : risiko && !risikoBasis.some((r) => r.id === risiko.id)
          ? [gewaehltesRisiko ?? { id: risiko.id, label: risiko.label || `Risiko #${risiko.id}` }, ...risikoBasis]
          : risikoBasis;

    const vertragBasis = risiko === null ? [] : alleVertraege ? vertraege : vertraegeZumRisiko;
    const alsZeile = (c: VertragEintrag): AuswahlEintrag => ({ id: c.id, label: c.titel, neben: c.neben });
    const vertragListe: AuswahlEintrag[] | null =
      vertragBasis === null
        ? null
        : vertrag && !vertragBasis.some((c) => c.id === vertrag.id)
          ? [{ id: vertrag.id, label: vertrag.label || `Vertrag #${vertrag.id}` }, ...vertragBasis.map(alsZeile)]
          : vertragBasis.map(alsZeile);
    const vertragWaehlen = (e: AuswahlEintrag | null) => {
      if (e === null) return onVertrag(null);
      const treffer = [...(vertraegeZumRisiko ?? []), ...(vertraege ?? [])].find((c) => c.id === e.id);
      // Gemerkt wird die Vertragsnummer (wie überall), nicht der Titel der Zeile.
      onVertrag({ id: e.id, label: treffer?.label ?? vertrag?.label ?? e.label });
    };

    return (
      <div className="smp-schadenart">
        <StepHead
          eyebrow="Schadenmeldung"
          title="Welches Risiko ist betroffen?"
          hint={
            kontakt?.label
              ? `Risiken und Verträge von ${kontakt.label}. Beides ist optional und lässt sich später ändern.`
              : "Risiko und Vertrag sind optional und lassen sich später ändern."
          }
        />
        {!kontakt ? (
          <section className="smp-karte">
            <div className="smp-hinweis">Erst einen Kontakt wählen.</div>
          </section>
        ) : (
          <>
            <section className="smp-karte" aria-label="Risiko">
              <div className="smp-karte-titel">
                Risiko <span className="smp-optional">optional</span>
              </div>
              <AuswahlListe
                name="Risiko"
                platzhalter="Risiko suchen (Name, Anschrift, Kennzeichen) …"
                eintraege={risikoListe}
                gewaehltId={risiko?.id ?? null}
                onWahl={risikoWaehlen}
                leer={
                  risiken !== null && risiken.length > 0 ? (
                    <>
                      <span>Beim Kontakt sind keine {artText} als Risiko hinterlegt.</span>
                    </>
                  ) : (
                    "Beim Kontakt ist kein Risiko hinterlegt."
                  )
                }
              />
              {artText !== null && risiken !== null && risiken.length > passend.length && (
                <button
                  type="button"
                  className="tbtn smp-auswahl-schalter"
                  onClick={() => setAlleRisiken((v) => !v)}
                >
                  {alleRisiken ? `Nur ${artText} zeigen` : "Alle Risiken des Kontakts zeigen"}
                </button>
              )}
            </section>

            <section className="smp-karte" aria-label="Vertrag">
              <div className="smp-karte-titel">
                Vertrag <span className="smp-optional">optional</span>
              </div>
              {risiko === null && <div className="smp-hinweis">Erst ein Risiko wählen.</div>}
              {(risiko !== null || vertrag !== null) && (
                <AuswahlListe
                  name="Vertrag"
                  platzhalter="Vertrag suchen (Sparte, Nummer, Versicherer) …"
                  eintraege={vertragListe}
                  gewaehltId={vertrag?.id ?? null}
                  onWahl={vertragWaehlen}
                  leer={
                    alleVertraege
                      ? "Beim Kontakt ist kein Vertrag hinterlegt."
                      : "Mit diesem Risiko ist kein Vertrag verknüpft."
                  }
                />
              )}
              {risiko !== null && (alleVertraege || (vertraegeZumRisiko !== null && vertraegeZumRisiko.length === 0)) && (
                <button
                  type="button"
                  className="tbtn smp-auswahl-schalter"
                  onClick={() => setAlleVertraege((v) => !v)}
                >
                  {alleVertraege ? "Nur Verträge zu diesem Risiko zeigen" : "Alle Verträge des Kontakts zeigen"}
                </button>
              )}
            </section>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="smp-schadenart">
      <section className="smp-karte">
        <div className="smp-feld">
          <div className="smp-feld-titel">
            Kontakt <span className="smp-pflicht">Pflicht</span>
          </div>
          {/* Vorauswahl ohne Namen (`/schaeden/neu?contactId=…`): die Suche lädt ihn nach. */}
          <KontaktSuche
            kontakt={kontakt}
            vorheriger={gespeicherterKontakt}
            onWahl={kontaktWaehlen}
            onName={onKontakt}
          />
        </div>
        {kontakt && (
          <>
            <ChipAuswahl titel="Risikoobjekt" eintraege={risiken} gewaehlt={risiko} onWahl={onRisiko} />
            <ChipAuswahl titel="Vertrag" eintraege={vertraege} gewaehlt={vertrag} onWahl={onVertrag} />
          </>
        )}
      </section>

      <StepType answers={answers} update={typAendern} />

      {answers.damageType && (
        <section className="smp-karte">
          <Sparte damageType={answers.damageType} wert={schadensart} onWert={onSchadensart} />
        </section>
      )}
    </div>
  );
}
